/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import { ApiError } from "./ApiError";
import type { ApiRequestOptions } from "./ApiRequestOptions";
import type { ApiResult } from "./ApiResult";
import { CancelablePromise } from "./CancelablePromise";
import type { OnCancel } from "./CancelablePromise";
import type { OpenAPIConfig } from "./OpenAPI";

const isDefined = <T>(
  value: T | null | undefined
): value is Exclude<T, null | undefined> => {
  return value !== undefined && value !== null;
};

const isString = (value: any): value is string => {
  return typeof value === "string";
};

const isStringWithValue = (value: any): value is string => {
  return isString(value) && value !== "";
};

const isBlob = (value: any): value is Blob => {
  return (
    typeof value === "object" &&
    typeof value.type === "string" &&
    typeof value.stream === "function" &&
    typeof value.arrayBuffer === "function" &&
    typeof value.constructor === "function" &&
    typeof value.constructor.name === "string" &&
    /^(Blob|File)$/.test(value.constructor.name) &&
    /^(Blob|File)$/.test(value[Symbol.toStringTag])
  );
};

const isFormData = (value: any): value is FormData => {
  return value instanceof FormData;
};

const base64 = (str: string): string => {
  try {
    return btoa(str);
  } catch (err) {
    // @ts-ignore
    return Buffer.from(str).toString("base64");
  }
};

const getQueryString = (params: Record<string, any>): string => {
  const qs: string[] = [];

  const append = (key: string, value: any) => {
    qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  const process = (key: string, value: any) => {
    if (isDefined(value)) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          process(key, v);
        });
      } else if (typeof value === "object") {
        Object.entries(value).forEach(([k, v]) => {
          process(`${key}[${k}]`, v);
        });
      } else {
        append(key, value);
      }
    }
  };

  Object.entries(params).forEach(([key, value]) => {
    process(key, value);
  });

  if (qs.length > 0) {
    return `?${qs.join("&")}`;
  }

  return "";
};

const getUrl = (config: OpenAPIConfig, options: ApiRequestOptions): string => {
  const encoder = config.ENCODE_PATH || encodeURI;

  const path = options.url
    .replace("{api-version}", config.VERSION)
    .replace(/{(.*?)}/g, (substring: string, group: string) => {
      if (options.path?.hasOwnProperty(group)) {
        return encoder(String(options.path[group]));
      }
      return substring;
    });

  const url = `${config.BASE}${path}`;
  if (options.query) {
    return `${url}${getQueryString(options.query)}`;
  }
  return url;
};

const getFormData = (options: ApiRequestOptions): FormData | undefined => {
  if (options.formData) {
    const formData = new FormData();

    const process = (key: string, value: any) => {
      if (isString(value) || isBlob(value)) {
        formData.append(key, value);
      } else {
        formData.append(key, JSON.stringify(value));
      }
    };

    Object.entries(options.formData)
      .filter(([_, value]) => isDefined(value))
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => process(key, v));
        } else {
          process(key, value);
        }
      });

    return formData;
  }
  return undefined;
};

type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;

const resolve = async <T>(
  options: ApiRequestOptions,
  resolver?: T | Resolver<T>
): Promise<T | undefined> => {
  if (typeof resolver === "function") {
    return (resolver as Resolver<T>)(options);
  }
  return resolver;
};

const getHeaders = async (
  config: OpenAPIConfig,
  options: ApiRequestOptions
): Promise<Headers> => {
  const token = await resolve(options, config.TOKEN);
  const username = await resolve(options, config.USERNAME);
  const password = await resolve(options, config.PASSWORD);
  const additionalHeaders = await resolve(options, config.HEADERS);

  const headers = Object.entries({
    Accept: "application/json",
    ...additionalHeaders,
    ...options.headers
  })
    .filter(([_, value]) => isDefined(value))
    .reduce(
      (headers, [key, value]) => ({
        ...headers,
        [key]: String(value)
      }),
      {} as Record<string, string>
    );

  if (isStringWithValue(token)) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (isStringWithValue(username) && isStringWithValue(password)) {
    const credentials = base64(`${username}:${password}`);
    headers["Authorization"] = `Basic ${credentials}`;
  }

  if (options.body) {
    if (options.mediaType) {
      headers["Content-Type"] = options.mediaType;
    } else if (isBlob(options.body)) {
      headers["Content-Type"] = options.body.type || "application/octet-stream";
    } else if (isString(options.body)) {
      headers["Content-Type"] = "text/plain";
    } else if (!isFormData(options.body)) {
      headers["Content-Type"] = "application/json";
    }
  }

  return new Headers(headers);
};

const getRequestBody = (options: ApiRequestOptions): any => {
  if (options.body) {
    if (options.mediaType?.includes("/json")) {
      return JSON.stringify(options.body);
    } else if (
      isString(options.body) ||
      isBlob(options.body) ||
      isFormData(options.body)
    ) {
      return options.body;
    } else {
      return JSON.stringify(options.body);
    }
  }
  return undefined;
};

// Requests that may be fired immediately before navigation. Sending them with
// `keepalive: true` lets the browser finish the request after the page
// unloads, which eliminates AWS ALB 460 ("client-abandoned") responses; a
// caller that awaits the same request can still use its response normally.
// Browsers cap total in-flight keepalive body size to 64 KB per origin, so we
// opt in only for small requests. The method is part of the match because one
// path can serve both a fire-and-forget call and one whose response we need
// (releasing vs. acquiring a case lock).
const KEEPALIVE_REQUESTS = [
  { method: "POST", urlSuffix: "/v1/analytics/track" },
  { method: "DELETE", urlSuffix: "/lock" }
] as const;

const shouldUseKeepalive = (method: string, url: string): boolean =>
  KEEPALIVE_REQUESTS.some(
    (candidate) =>
      candidate.method === method && url.endsWith(candidate.urlSuffix)
  );

// Endpoints that reflect just-written data and must never be served from the
// browser's HTTP cache. The meal stats aggregate is fetched with an identical
// URL each time (`?type=day&date=<today>`), so after a meal is added/deleted a
// cached response would replay the pre-change totals. `no-store` forces a fresh
// read. Matched on the path segment since the URL carries a query string.
const NO_STORE_URL_SEGMENTS = ["/v1/mealinsight/stats"] as const;

const shouldBypassCache = (url: string): boolean =>
  NO_STORE_URL_SEGMENTS.some((segment) => url.includes(segment));

// Per-endpoint request timeouts, falling back to DEFAULT_TIMEOUT_MS. File
// transfers get a longer allowance since they can take a while on slow
// connections. Matched on the path segment so downloads and previews
// (`/v1/files/{id}`) get it too, not just the upload endpoint.
const URL_TIMEOUT_SEGMENTS: Record<string, number> = {
  "/v1/files": 120_000
};

const DEFAULT_TIMEOUT_MS = 15_000;

const getTimeoutMs = (url: string): number => {
  const segment = Object.keys(URL_TIMEOUT_SEGMENTS).find((s) =>
    url.includes(s)
  );
  return segment ? URL_TIMEOUT_SEGMENTS[segment] : DEFAULT_TIMEOUT_MS;
};

// Thrown when a request is aborted by its timeout signal, so callers can
// distinguish a timeout from a generic network failure.
export class RequestTimeoutError extends Error {
  constructor(url: string, timeoutMs: number) {
    super(`Request to ${url} timed out after ${timeoutMs}ms`);
    this.name = "RequestTimeoutError";
  }
}

export const sendRequest = async (
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Headers,
  onCancel: OnCancel
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutMs = getTimeoutMs(url);
  // AbortSignal.any/timeout require Safari 17.4/16.0+; the app's iOS 14 floor
  // (see ios/App/Podfile) has older WebKit versions that lack them entirely,
  // and calling either throws synchronously. Fall back to no timeout there
  // rather than breaking every request on those devices.
  //
  // Known limitation: on that fallback path, timeoutMs is not enforced at
  // all - only explicit cancellation via onCancel aborts the request. A
  // hung request/response-body read on legacy WebKit can stay pending
  // indefinitely. Accepted trade-off for now (see docs/features/user-app/
  // app-access-gate/CHANGELOG.md); revisit with a timer-based abort
  // fallback if legacy-WebKit support becomes a bigger concern.
  const supportsAbortSignalTimeout =
    typeof AbortSignal.any === "function" &&
    typeof AbortSignal.timeout === "function";
  const signal = supportsAbortSignalTimeout
    ? AbortSignal.any([controller.signal, AbortSignal.timeout(timeoutMs)])
    : controller.signal;

  const request: RequestInit = {
    headers,
    body: body ?? formData,
    method: options.method,
    signal
  };

  if (shouldUseKeepalive(options.method, url)) {
    request.keepalive = true;
  }

  if (shouldBypassCache(url)) {
    request.cache = "no-store";
  }

  if (config.WITH_CREDENTIALS) {
    request.credentials = config.CREDENTIALS;
  }

  onCancel(() => controller.abort());

  try {
    return await fetch(url, request);
  } catch (error) {
    // AbortSignal.timeout() rejects fetch with a TimeoutError DOMException;
    // controller.abort() (explicit cancellation) rejects with AbortError.
    // Only the former should surface as a RequestTimeoutError.
    if (error instanceof DOMException && error.name === "TimeoutError") {
      throw new RequestTimeoutError(url, timeoutMs);
    }
    throw error;
  }
};

export const getResponseBody = async (
  response: Response,
  url: string,
  timeoutMs: number
): Promise<any> => {
  if (response.status !== 204) {
    try {
      const contentType = response.headers.get("Content-Type");
      if (contentType) {
        const isJSON = contentType.toLowerCase().startsWith("application/json");
        if (isJSON) {
          return await response.json();
        } else {
          return await response.text();
        }
      }
    } catch (error) {
      // The abort signal can fire mid-stream, after fetch() has already
      // resolved with headers. That must still surface as a timeout, not
      // silently resolve as an empty successful response.
      if (error instanceof DOMException && error.name === "TimeoutError") {
        throw new RequestTimeoutError(url, timeoutMs);
      }
      throw error;
    }
  }
  return undefined;
};

const catchErrorCodes = (
  options: ApiRequestOptions,
  result: ApiResult
): void => {
  const errors: Record<number, string> = {
    400: "Bad Request",
    401: "Unauthorized",
    403: "Forbidden",
    404: "Not Found",
    500: "Internal Server Error",
    502: "Bad Gateway",
    503: "Service Unavailable",
    ...options.errors
  };

  const error = errors[result.status];
  if (error) {
    throw new ApiError(options, result, error);
  }

  if (!result.ok) {
    throw new ApiError(options, result, "Generic Error");
  }
};

/**
 * Request method
 * @param config The OpenAPI configuration object
 * @param options The request options from the service
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export const request = <T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions
): CancelablePromise<T> => {
  return new CancelablePromise(async (resolve, reject, onCancel) => {
    try {
      const url = getUrl(config, options);
      const formData = getFormData(options);
      const body = getRequestBody(options);
      const headers = await getHeaders(config, options);

      if (!onCancel.isCancelled) {
        const response = await sendRequest(
          config,
          options,
          url,
          body,
          formData,
          headers,
          onCancel
        );
        const responseBody = await getResponseBody(
          response,
          url,
          getTimeoutMs(url)
        );

        const result: ApiResult = {
          url,
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          body: responseBody,
          headers: response.headers
        };

        catchErrorCodes(options, result);

        resolve(result.body);
      }
    } catch (error) {
      reject(error);
    }
  });
};
